/**
 * @fileoverview Project Page
 * @description Test cycles belonging to one evaluation project.
 */

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CalendarRange,
  Clock,
  FolderKanban,
  Layers,
  MoreVertical,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  EmptyState,
  EvaluationBreadcrumbs,
  MissingRecord,
  SummaryMetric,
} from '../components/common';
import { NameDialog } from '../components/NameDialog';
import {
  createCycle,
  cycleStats,
  deleteCycle,
  projectStats,
  updateCycle,
} from '../utils/evaluationStore';

/**
 * Render one test cycle row.
 * @param {Object} props - Cycle record and row actions.
 * @returns {JSX.Element} Cycle card.
 */
function CycleCard({ cycle, onOpen, onEdit, onDelete }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const stats = cycleStats(cycle);

  return (
    <Paper className="aier-scenario-card" elevation={0}>
      <Box
        className="aier-scenario-body"
        role="button"
        tabIndex={0}
        onClick={() => onOpen(cycle)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpen(cycle);
          }
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" minWidth={0}>
          <Box className="aier-cycle-icon">
            <CalendarRange size={18} />
          </Box>
          <Box className="aier-scenario-main">
            <Typography className="aier-scenario-name">{cycle.name}</Typography>
            <Typography className="aier-scenario-description">
              {cycle.description || 'No description'}
            </Typography>
            <Stack direction="row" spacing={0.75} mt={1.25} flexWrap="wrap" useFlexGap>
              <Chip size="small" variant="outlined" icon={<Layers size={12} />} label={`${stats.scenarioCount} scenarios`} />
              <Chip size="small" variant="outlined" icon={<Play size={12} />} label={`${stats.runCount} runs`} />
              {stats.lastActivity && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Latest run ${new Date(stats.lastActivity).toLocaleDateString()}`}
                />
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <IconButton
        size="small"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        aria-label={`Actions for ${cycle.name}`}
      >
        <MoreVertical size={16} />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setMenuAnchor(null); onEdit(cycle); }}>
          <Pencil size={14} style={{ marginRight: 8 }} /> Rename
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(cycle); }} sx={{ color: '#C81E3A' }}>
          <Trash2 size={14} style={{ marginRight: 8 }} /> Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
}

/**
 * Render the test cycles inside a project.
 * @param {Object} props - Workspace projects and change handler.
 * @returns {JSX.Element} Project page.
 */
export function ProjectPage({ projects, onProjectsChange }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [dialogState, setDialogState] = useState(null);
  const project = projects.find((item) => item.id === projectId);

  if (!project) {
    return <MissingRecord icon={FolderKanban} message="This project no longer exists." />;
  }

  const stats = projectStats(project);

  /**
   * Persist the dialog result as a new or renamed cycle.
   * @param {{name: string, description: string}} values - Submitted values.
   * @returns {void}
   */
  const handleDialogSubmit = (values) => {
    onProjectsChange(dialogState.cycle
      ? updateCycle(project.id, dialogState.cycle.id, values)
      : createCycle(project.id, values));
    setDialogState(null);
  };

  /**
   * Open the dialog with the next cycle number suggested.
   * @returns {void}
   */
  const openNewCycleDialog = () => {
    setDialogState({ cycle: null, defaults: { name: `Cycle ${project.cycles.length + 1}` } });
  };

  return (
    <>
      <EvaluationBreadcrumbs trail={[{ label: project.name }]} />

      <Box className="aier-header">
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box className="aier-project-icon" sx={{ backgroundColor: `${project.color}14`, color: project.color }}>
            <FolderKanban size={22} />
          </Box>
          <Box>
            <Typography variant="h4" component="h1">{project.name}</Typography>
            <Typography color="text.secondary">
              {project.description || 'Track each test cycle and the scenarios evaluated within it.'}
            </Typography>
          </Box>
        </Stack>
        {project.cycles.length > 0 && (
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNewCycleDialog}>
            New test cycle
          </Button>
        )}
      </Box>

      {project.cycles.length === 0 ? (
        <EmptyState
          icon={CalendarRange}
          title="Add your first test cycle"
          message="A test cycle groups the scenarios evaluated together — for example a release, sprint, or model version. Scenarios and their runs live inside a cycle."
        >
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNewCycleDialog}>
            New test cycle
          </Button>
        </EmptyState>
      ) : (
        <>
          <Box className="aier-summary-grid aier-summary-grid--four" aria-label="Project summary">
            <SummaryMetric
              label="Test cycles"
              value={stats.cycleCount}
              caption="In this project"
              icon={CalendarRange}
              color="#01426A"
            />
            <SummaryMetric
              label="Scenarios"
              value={stats.scenarioCount}
              caption="Across all cycles"
              icon={Layers}
              color="#7C3AED"
            />
            <SummaryMetric
              label="Runs"
              value={stats.runCount}
              caption="Across all scenarios"
              icon={Play}
              color="#0097A9"
            />
            <SummaryMetric
              label="Last activity"
              value={stats.lastActivity ? new Date(stats.lastActivity).toLocaleDateString() : '—'}
              caption="Most recent run recorded"
              icon={Clock}
              color="#008653"
            />
          </Box>

          <Stack spacing={1.5}>
            {project.cycles.map((cycle) => (
              <CycleCard
                key={cycle.id}
                cycle={cycle}
                onOpen={(target) => navigate(target.id)}
                onEdit={(target) => setDialogState({ cycle: target })}
                onDelete={(target) => onProjectsChange(deleteCycle(project.id, target.id))}
              />
            ))}
          </Stack>
        </>
      )}

      <NameDialog
        open={Boolean(dialogState)}
        title={dialogState?.cycle ? 'Rename test cycle' : 'New test cycle'}
        nameLabel="Test cycle"
        initialValues={dialogState?.cycle || dialogState?.defaults}
        onCancel={() => setDialogState(null)}
        onSubmit={handleDialogSubmit}
      />
    </>
  );
}

export default ProjectPage;
